import { RequestData } from '@ant-design/pro-table';
import { stringify } from 'qs';
import { request } from '@/utils/request';
import { ClientDocumentDetailDto } from './client-documents';
import { GeneralInstructionStatus } from './general-instruction';

export interface FundInstructionListItem {
  instructionId: string;
  date: string;
  userId: string;
  userName: string;
  userAccountNumber: string;
  rmId: string;
  rmName: string;
  intent: string;
  fundName: string;
  fundId: string;
  dividendOption?: string;
  unit?: number;
  amount: number;
  status: GeneralInstructionStatus;
  refId: string;
  remark?: string;
  currency: string;
}

export enum FundInstructionIntent {
  SUBSCRIPTION = 'SUBSCRIPTION',
  REDEMPTION = 'REDEMPTION',
  DIVIDEND_PAYOUT = 'DIVIDEND_PAYOUT',
  OPEN_ACCOUNT = 'OPEN_ACCOUNT'
}

export enum IFastFundInstructionIntent {
  IFAST_FUND_SUBSCRIPTION = 'IFAST_FUND_SUBSCRIPTION',
  IFAST_FUND_REDEMPTION = 'IFAST_FUND_REDEMPTION',
  IFAST_FUND_OPEN_ACCOUNT = 'IFAST_FUND_OPEN_ACCOUNT',
  IFAST_FUND_DIVIDEND_PAYOUT = 'IFAST_FUND_DIVIDEND_PAYOUT'
}

export enum DividendOption {
  CASH = 'CASH',
  REINVEST = 'REINVEST'
}

type FundInstructionSorter = 'data_desc' | 'data_asc';
export interface FundInstructionQueryParams {
  date?: string[];
  current?: number;
  pageSize?: number;
  userId?: string;
  sorter?: FundInstructionSorter;
  relationshipManagerId?: string[];
  status?: GeneralInstructionStatus;
  fundName?: string;
  intent?: string[];
  dateFrom?: string;
  dateTo?: string;
  refId?: string;
}

export interface FundPositionListItem {
  userId: string;
  userName: string;
  userAccountNumber: string;
  rmId: string;
  rmName: string;
  fundName: string;
  units: number;
  fundCurrency: string;
  marketValue: number;
  return: number;
  percentChange: number;
}

export interface FundPositionQueryParams {
  current?: number;
  pageSize?: number;
  userId?: string[];
  relationshipManagerId?: string[];
  fundName?: string;
  fundCurrency?: string;
}

export async function queryFundInstructions(params: FundInstructionQueryParams): Promise<RequestData<FundInstructionListItem>> {
  const queryString = stringify(params, { arrayFormat: 'brackets' });
  return request<RequestData<FundInstructionListItem>>(`/funds/instructions?${queryString}`, {
    method: 'GET'
  });
}

export async function queryFundPositions(params: FundPositionQueryParams): Promise<RequestData<FundPositionListItem>> {
  const queryString = stringify(params, { arrayFormat: 'brackets' });
  return await request<RequestData<FundPositionListItem>>(`/funds/positions?${queryString}`, {
    method: 'GET'
  });
}

type FundInstructionClientDocument = ClientDocumentDetailDto & {
  details: {
    [key: string]: ClientDocumentDetailDto;
  };
};

export interface FundInstructionDetail {
  id: string;
  refId: string;
  date: string; // ISO 8601
  status: string;
  subStatus?: string;
  intent: IFastFundInstructionIntent;
  description: string; // e.g., "Fund Subscription"
  fundName: string;
  dividendOption?: 'CASH' | 'REINVEST';
  entryType: 'CREDIT' | 'DEBIT' | 'NONE';
  // Subscription fields
  subscriptionAmount?: number;
  // Redemption fields
  redeemUnit?: number;
  executedRedemptionAmount?: number | null;
  // Dividend fields
  dividendPayoutPerUnit?: number;
  creditedUnits?: number; // For reinvestment
  // Common execution fields
  debitAmount?: number | null; // Final debit amount (null if redemption in progress)
  creditAmount?: number | null; // Final credit amount (null if redemption in progress)
  debitAccountId?: string;
  debitAccountName?: string;
  debitAccountNumber?: string;
  creditAccountId?: string;
  creditAccountName?: string;
  creditAccountNumber?: string;
  executionDate?: string; // ISO 8601
  executedNav?: number;
  subscribedUnits?: number;
  // iFast sub account number used for this instruction
  iFastSubAccountNumber?: string | null;
  // Additional fields
  userId: string;
  currency: string;
  remarks?: string;
  metadata?: Record<string, string>;
  clientDocuments?: FundInstructionClientDocument[];
  feeAmount?: number;
}

export function getFundInstructionDetail(params: { id: string }): Promise<FundInstructionDetail> {
  return request<FundInstructionDetail>(`/funds/instructions/${params.id}`, {
    method: 'GET'
  });
}

export interface CancelAccountOpeningParams {
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface CancelAccountOpeningResponse {
  success: boolean;
}

export function cancelAccountOpeningInstruction(
  instructionId: string,
  params: CancelAccountOpeningParams
): Promise<CancelAccountOpeningResponse> {
  return request<CancelAccountOpeningResponse>(`/funds/instructions/account-opening/${instructionId}/cancel`, {
    method: 'POST',
    data: params
  });
}

/**
 * Get pending fund instructions count
 * Filters by status: NEW, PENDING_APPROVE
 */
export async function queryFundInstructionsPendingCount(): Promise<number> {
  const queryString = stringify(
    {
      current: 1,
      pageSize: 1,
      status: 'NEW'
    },
    { arrayFormat: 'brackets' }
  );
  const response = await request<RequestData<FundInstructionListItem>>(`/funds/instructions?${queryString}`, {
    method: 'GET'
  });
  return response.total ?? 0;
}
